import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, Maximize2, Users, Paperclip } from 'lucide-react';
import { MAIL_API, mailAuth, addrLabel } from './mailUtils';
import { buildDraft } from './MailComposer';
import { parseAddrs } from './RecipientInput';
import { TemplatePicker } from './MailTemplates';
import { useAuth } from '../../App';

/** One-line reply under the newest message: type, Ctrl/⌘+Enter, done. Expands into the full composer when you need more. */
export const QuickReply = ({ last, me, chips = [], onSent, onExpand, canSend }) => {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [all, setAll] = useState(false);
  const [sending, setSending] = useState(false);
  const ref = useRef(null);
  const draft = buildDraft('reply', last, me, all);
  const toList = parseAddrs(draft.to);
  const ccList = parseAddrs(draft.cc);
  const canAll = parseAddrs(buildDraft('reply', last, me, true).cc).length > 0;

  useEffect(() => { setText(''); setAll(false); }, [last?.id]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [text]);

  const send = async () => {
    if (!text.trim() || sending) return;
    if (/\{\{\s*\w+\s*\}\}/.test(text)) { toast.error('Fill in the {{placeholders}} from the template first'); return; }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('to', draft.to); fd.append('cc', draft.cc); fd.append('bcc', ''); fd.append('subject', draft.subject || '');
      fd.append('body', text); fd.append('body_html', ''); fd.append('mode', 'reply');
      fd.append('thread_id', draft.thread_id || ''); fd.append('message_id', draft.message_id || ''); fd.append('include_attachments', '0');
      const r = await axios.post(`${MAIL_API}/send`, fd, { ...mailAuth(), timeout: 120000 });
      toast.success('Reply sent');
      if (r.data.thread_id) axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/daily-brief/done-thread`, { thread_id: r.data.thread_id }, mailAuth()).catch(() => {});
      setText('');
      onSent?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? `${d.message} ${d.action || ''}` : d || 'Could not send');
    }
    setSending(false);
  };
  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); send(); }
    if (e.key === 'Escape') ref.current?.blur();
  };
  const expand = () => onExpand({ ...draft, body: text });
  const insertTemplate = ({ body_html }) => {
    const plain = body_html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|li)>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\n{3,}/g, '\n\n').trim();
    setText(t => (t ? `${t}\n${plain}` : plain));
    setTimeout(() => ref.current?.focus(), 0);
  };
  if (!canSend || !last) return null;
  const who = toList.map(a => a.name || addrLabel(a)).join(', ');
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-100 transition-shadow" data-testid="mail-quick-reply">
      <div className="flex items-center gap-2 px-3 pt-2 text-[11px] text-slate-500">
        <span className="truncate" data-testid="mail-quick-reply-to">to <span className="font-semibold text-slate-700">{who || '—'}</span>{ccList.length > 0 && <span> · cc {ccList.map(a => a.name || addrLabel(a)).join(', ')}</span>}</span>
        {canAll && (
          <button type="button" onClick={() => setAll(a => !a)} className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-bold ${all ? 'bg-sky-50 border-sky-300 text-sky-800' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`} data-testid="mail-quick-reply-all">
            <Users className="w-3 h-3" />{all ? 'Replying to all' : 'Reply all'}
          </button>
        )}
      </div>
      <div className="flex items-end gap-2 px-3 pb-2 pt-1">
        <textarea ref={ref} value={text} onChange={e => setText(e.target.value)} onKeyDown={onKey} rows={1} placeholder={`Reply to ${who || 'them'}…  (${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to send)`}
          className="flex-1 resize-none bg-transparent text-sm text-slate-900 placeholder:text-slate-400 outline-none leading-relaxed py-1.5 max-h-[180px]" data-testid="mail-quick-reply-input" />
        <div className="flex items-center gap-0.5 shrink-0 pb-0.5">
          <TemplatePicker compact ctx={{ to: draft.to, chips, myName: user?.full_name || '' }} onInsert={insertTemplate} currentBody="" />
          <button type="button" onClick={expand} title="Open the full composer (formatting, images, attachments)" className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-200/70 hover:text-slate-900" data-testid="mail-quick-reply-expand"><Maximize2 className="w-4 h-4" /></button>
          <button type="button" onClick={expand} title="Attach a file (opens the full composer)" className="p-1.5 rounded-lg text-slate-600 hover:bg-slate-200/70 hover:text-slate-900 hidden sm:inline-flex" data-testid="mail-quick-reply-attach"><Paperclip className="w-4 h-4" /></button>
          <button type="button" onClick={send} disabled={!text.trim() || sending} className="ml-1 h-8 px-3 rounded-lg bg-sky-600 hover:bg-sky-500 disabled:bg-slate-200 disabled:text-slate-400 text-white text-xs font-bold inline-flex items-center gap-1.5" data-testid="mail-quick-reply-send">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}Send
          </button>
        </div>
      </div>
    </div>
  );
};
